import { incidentCanonicalReviewOutcome } from '../../components/incidents/reviewCanonical';
import type { SafetyWorkflowStepId, SafetyWorkflowProgress } from '../safetyWorkflowStepMapping';
import { ownerRoleIsUnassigned } from './incidentPatternVocabulary';
import {
  GROUPED_CORRECTIVE_ACTION_LOCKED_DESCRIPTION,
  GROUPED_CORRECTIVE_ACTION_UNLOCKED_DESCRIPTION,
  GROUPED_IMPACT_TRACKING_LOCKED_DESCRIPTION,
  GROUPED_IMPACT_TRACKING_UNLOCKED_DESCRIPTION,
  GROUPED_SAFETY_CASE_STEP_LABELS,
} from './groupedSafetyCaseStepLabels';
import type { GroupedCorrectiveWorkflowPhase } from './groupedCorrectiveActionWorkflow';
import { groupedCorrectiveWorkflowPhaseLabel } from './groupedCorrectiveActionWorkflow';
import { PATTERN_OPEN_SAFETY_CASE_ACTION } from './patternOwnershipContainment';

export const EXPLICIT_REPRESENTATIVE_REVIEW_OUTCOMES = new Set([
  'true_event',
  'false_positive',
  'insufficient_evidence',
  'duplicate',
  'not_actionable',
  'expected_activity',
  'demo_or_test_activity',
]);

export function readRepresentativeReviewOutcome(
  incident: Record<string, unknown> | null | undefined,
): string {
  const topLevel = String((incident as { review_outcome?: string })?.review_outcome || '').trim().toLowerCase();
  if (topLevel) return topLevel;
  return incidentCanonicalReviewOutcome(incident);
}

/** True only when the representative incident has an explicit review / verification outcome. */
export function isExplicitRepresentativeReviewComplete(
  incident: Record<string, unknown> | null | undefined,
): boolean {
  const outcome = readRepresentativeReviewOutcome(incident);
  if (!outcome || outcome === 'needs_review') return false;
  if (EXPLICIT_REPRESENTATIVE_REVIEW_OUTCOMES.has(outcome)) return true;
  const source = String(
    (incident as { review_outcome_source?: string })?.review_outcome_source
    || (incident as { safety_case?: { review?: { review_outcome_source?: string } } })?.safety_case?.review
      ?.review_outcome_source
    || '',
  ).trim().toLowerCase();
  return source === 'explicit';
}

export type GroupedGuidedPanelId =
  | 'understand_risk'
  | 'verify_evidence'
  | 'decide_action'
  | 'track_impact'
  | 'report';

export type GroupedGuidedPanelDefinition = {
  id: GroupedGuidedPanelId;
  workflowStepId: SafetyWorkflowStepId;
  label: string;
  description: string;
  primaryActionLabel: string;
};

export const GROUPED_GUIDED_PANELS: GroupedGuidedPanelDefinition[] = [
  {
    id: 'understand_risk',
    workflowStepId: 'detected',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.riskPattern,
    description: 'What repeated, where it occurred, and how often it was observed.',
    primaryActionLabel: `Continue to ${GROUPED_SAFETY_CASE_STEP_LABELS.evidenceReview.toLowerCase()}`,
  },
  {
    id: 'verify_evidence',
    workflowStepId: 'review',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.evidenceReview,
    description: 'Confirm whether the representative evidence shows a recurring risk pattern that requires ongoing management.',
    primaryActionLabel: `Open ${GROUPED_SAFETY_CASE_STEP_LABELS.evidenceReview.toLowerCase()}`,
  },
  {
    id: 'decide_action',
    workflowStepId: 'assign_action',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.correctiveAction,
    description: GROUPED_CORRECTIVE_ACTION_UNLOCKED_DESCRIPTION,
    primaryActionLabel: `Next: ${GROUPED_SAFETY_CASE_STEP_LABELS.impactTracking}`,
  },
  {
    id: 'track_impact',
    workflowStepId: 'track_impact',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.impactTracking,
    description: GROUPED_IMPACT_TRACKING_UNLOCKED_DESCRIPTION,
    primaryActionLabel: `Continue to ${GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport}`,
  },
  {
    id: 'report',
    workflowStepId: 'report',
    label: GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport,
    description: 'Generate a draft safety report once required steps are complete.',
    primaryActionLabel: `Generate ${GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport}`,
  },
];

export function groupedGuidedPanelForWorkflowStep(
  stepId: SafetyWorkflowStepId,
): GroupedGuidedPanelId {
  const match = GROUPED_GUIDED_PANELS.find((panel) => panel.workflowStepId === stepId);
  return match?.id || 'understand_risk';
}

export function defaultGroupedGuidedPanel(progress: SafetyWorkflowProgress): GroupedGuidedPanelId {
  return groupedGuidedPanelForWorkflowStep(progress.currentStepId);
}

export type GuidedWorkflowStepDisplayStatus = 'done' | 'current' | 'pending' | 'locked' | 'blocked';

export function groupedCorrectiveActionPanelDescription(correctiveActionLocked?: boolean): string {
  return correctiveActionLocked
    ? GROUPED_CORRECTIVE_ACTION_LOCKED_DESCRIPTION
    : GROUPED_CORRECTIVE_ACTION_UNLOCKED_DESCRIPTION;
}

export function groupedImpactTrackingPanelDescription(impactTrackingLocked?: boolean): string {
  return impactTrackingLocked
    ? GROUPED_IMPACT_TRACKING_LOCKED_DESCRIPTION
    : GROUPED_IMPACT_TRACKING_UNLOCKED_DESCRIPTION;
}

export function groupedImpactTrackingLocked(args: {
  correctiveActionLocked?: boolean;
  assignActionComplete?: boolean;
}): boolean {
  if (args.correctiveActionLocked) return true;
  return !args.assignActionComplete;
}

export function groupedGuidedPanelShowsPrimaryAction(
  panelId: GroupedGuidedPanelId,
  status: GuidedWorkflowStepDisplayStatus,
  context?: GroupedGuidedPanelStatusContext,
): boolean {
  // Understand risk stacks above Verify evidence; a footer CTA duplicates the next accordion.
  if (panelId === 'understand_risk') return false;
  // Already on Verify evidence — panel content is the work surface.
  if (panelId === 'verify_evidence' && status === 'current') return false;
  // Assign Action — owner + work order live in panel sections; footer only navigates forward.
  if (panelId === 'decide_action') {
    if (context?.hasLinkedSafetyCase) return false;
    if (status === 'done' || status === 'blocked' || status === 'locked') return false;
    if (context?.correctiveActionLocked) return false;
    if (!['completed', 'validated'].includes(String(context?.correctiveWorkflowPhase || ''))) return false;
    if (ownerRoleIsUnassigned(context?.followUpOwnerLabel)) return false;
    return true;
  }
  // Track Impact — measurement form is the work surface; footer only advances after save.
  if (panelId === 'track_impact') {
    if (status === 'done' || status === 'blocked' || status === 'locked') return false;
    if (status === 'current') return false;
    if (!context?.followUpMeasurementRecorded) return false;
    return true;
  }
  return status !== 'done' && status !== 'blocked';
}

export function guidedWorkflowStepDisplayStatus(
  stepId: SafetyWorkflowStepId,
  progress: SafetyWorkflowProgress,
  options?: { blockedStepIds?: SafetyWorkflowStepId[]; lockedStepIds?: SafetyWorkflowStepId[] },
): GuidedWorkflowStepDisplayStatus {
  const blocked = new Set(options?.blockedStepIds || []);
  const locked = new Set(options?.lockedStepIds || []);
  if (blocked.has(stepId)) return 'blocked';
  if (locked.has(stepId)) return 'locked';
  if (progress.completedStepIds.includes(stepId)) return 'done';
  if (progress.currentStepId === stepId) return 'current';
  return 'pending';
}

export function guidedWorkflowStatusLabel(status: GuidedWorkflowStepDisplayStatus): string {
  switch (status) {
    case 'done':
      return 'Done';
    case 'current':
      return 'Current';
    case 'locked':
      return 'Locked';
    case 'blocked':
      return 'Blocked';
    default:
      return 'Pending';
  }
}

export type GroupedGuidedPanelStatusContext = {
  representativeReviewComplete?: boolean;
  evidenceConfirmed?: boolean;
  correctiveActionLocked?: boolean;
  correctiveWorkflowPhase?: GroupedCorrectiveWorkflowPhase;
  recurringRiskIdentified?: boolean;
  reportDraftAvailable?: boolean;
  reportSignOffBlocked?: boolean;
  followUpOwnerLabel?: string | null;
  trackImpactReady?: boolean;
  followUpMeasurementRecorded?: boolean;
  hasLinkedSafetyCase?: boolean;
};

export function groupedGuidedPanelPrimaryActionLabel(
  panelId: GroupedGuidedPanelId,
  defaultLabel: string,
  context?: GroupedGuidedPanelStatusContext,
): string {
  if (panelId === 'decide_action') {
    if (context?.hasLinkedSafetyCase) return PATTERN_OPEN_SAFETY_CASE_ACTION;
    return `Next: ${GROUPED_SAFETY_CASE_STEP_LABELS.impactTracking}`;
  }
  if (panelId === 'track_impact') {
    return `Continue to ${GROUPED_SAFETY_CASE_STEP_LABELS.safetyReport}`;
  }
  return defaultLabel;
}

/**
 * Panel display status decoupled from macro step completion where needed.
 * Understand risk is never “done” from browser checklist state.
 */
export function groupedGuidedPanelDisplayStatus(
  panelId: GroupedGuidedPanelId,
  progress: SafetyWorkflowProgress,
  options?: {
    blockedStepIds?: SafetyWorkflowStepId[];
    lockedStepIds?: SafetyWorkflowStepId[];
    correctiveActionLocked?: boolean;
    correctiveWorkflowPhase?: GroupedCorrectiveWorkflowPhase;
  },
): GuidedWorkflowStepDisplayStatus {
  if (panelId === 'understand_risk') {
    if (progress.completedStepIds.includes('review')) return 'pending';
    if (progress.currentStepId === 'detected') return 'current';
    if (progress.completedStepIds.includes('detected')) return 'pending';
    return 'pending';
  }

  if (panelId === 'verify_evidence') {
    if (progress.currentStepId === 'review') return 'current';
  }

  if (panelId === 'decide_action') {
    if (options?.correctiveActionLocked) return 'locked';
    const phase = options?.correctiveWorkflowPhase;
    if (phase === 'completed' || phase === 'validated') return 'done';
    if (phase === 'current' || phase === 'planned' || phase === 'started') {
      return 'current';
    }
    if (phase === 'locked') return 'locked';
  }

  if (panelId === 'track_impact') {
    if (options?.correctiveActionLocked) return 'locked';
    if (!progress.completedStepIds.includes('assign_action')) {
      return progress.currentStepId === 'track_impact' ? 'current' : 'locked';
    }
  }

  const panel = GROUPED_GUIDED_PANELS.find((entry) => entry.id === panelId);
  if (!panel) return 'pending';
  return guidedWorkflowStepDisplayStatus(panel.workflowStepId, progress, options);
}

/** “Reviewed” footer only for server-backed representative verification. */
export function groupedGuidedPanelShowsReviewedFooter(
  panelId: GroupedGuidedPanelId,
  status: GuidedWorkflowStepDisplayStatus,
  context?: GroupedGuidedPanelStatusContext,
): boolean {
  if (panelId === 'verify_evidence') {
    return status === 'done' && Boolean(context?.representativeReviewComplete);
  }
  return false;
}

/** Panel badge labels for grouped guided workflow (human Done vs context Ready). */
export function groupedGuidedPanelStatusLabel(
  panelId: GroupedGuidedPanelId,
  status: GuidedWorkflowStepDisplayStatus,
  context?: GroupedGuidedPanelStatusContext,
): string {
  if (panelId === 'understand_risk') {
    if (status === 'current') return 'Current';
    if (context?.recurringRiskIdentified) return 'Identified';
    return 'Available';
  }

  if (panelId === 'verify_evidence') {
    if (status === 'current') return 'Current';
    if (status === 'done') {
      return context?.representativeReviewComplete ? 'Done' : 'Ready';
    }
    return 'Ready';
  }

  if (panelId === 'decide_action') {
    if (status === 'locked' || context?.correctiveActionLocked || context?.correctiveWorkflowPhase === 'locked') {
      return 'Locked';
    }
    if (ownerRoleIsUnassigned(context?.followUpOwnerLabel)) {
      return 'Pending';
    }
    if (context?.correctiveWorkflowPhase) {
      return groupedCorrectiveWorkflowPhaseLabel(context.correctiveWorkflowPhase);
    }
    if (status === 'current') return 'Current';
    return guidedWorkflowStatusLabel(status);
  }

  if (panelId === 'track_impact') {
    if (status === 'locked') return 'Locked';
    if (status === 'done') {
      return context?.followUpMeasurementRecorded ? 'Done' : 'Ready';
    }
    if (!context?.trackImpactReady) {
      if (status === 'current') return 'Current';
      return 'Pending';
    }
    if (status === 'current') return 'Current';
    return context?.followUpMeasurementRecorded ? 'Done' : 'Ready';
  }

  if (panelId === 'report') {
    const draftAvailable = Boolean(context?.reportDraftAvailable);
    const signOffBlocked = Boolean(context?.reportSignOffBlocked);
    if (draftAvailable && !signOffBlocked) {
      return 'Draft ready';
    }
    if (draftAvailable && signOffBlocked) {
      return 'Draft available';
    }
    if (!draftAvailable && (status === 'blocked' || signOffBlocked)) {
      return 'Pending required steps';
    }
  }

  return guidedWorkflowStatusLabel(status);
}

export type GroupedGuidedReportPanelStatus = {
  draftLabel: string;
  signOffLabel?: string;
};

export function groupedCorrectiveActionLocked(args: {
  representativeReviewComplete?: boolean;
  evidenceConfirmed?: boolean;
}): boolean {
  if (args.evidenceConfirmed) return false;
  return true;
}

export function groupedGuidedReportPanelStatus(
  context?: GroupedGuidedPanelStatusContext,
): GroupedGuidedReportPanelStatus | null {
  const draftAvailable = Boolean(context?.reportDraftAvailable);
  const signOffBlocked = Boolean(context?.reportSignOffBlocked);
  if (draftAvailable && signOffBlocked) {
    return { draftLabel: 'Draft report: available', signOffLabel: 'Sign-off: blocked' };
  }
  if (!draftAvailable && signOffBlocked) {
    return { draftLabel: 'Report preparation: Pending required steps' };
  }
  return null;
}
